import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../services/api';

// Estado inicial
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: true,
  isAuthenticated: false
};

// Tipos de ações
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_TOKEN: 'SET_TOKEN',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_AUTH: 'CLEAR_AUTH'
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case AUTH_ACTIONS.SET_USER:
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false
      };
    
    case AUTH_ACTIONS.SET_TOKEN:
      return { ...state, token: action.payload };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    case AUTH_ACTIONS.LOGOUT:
    case AUTH_ACTIONS.CLEAR_AUTH:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    
    default:
      return state;
  }
}

// Contexto
const AuthContext = createContext();

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  // Função de logout
  const logout = useCallback(() => {
    // Limpar localStorage
    localStorage.removeItem('token');
    
    // Limpar headers do axios
    delete api.defaults.headers.common['Authorization'];
    
    // Limpar estado
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    
    // Redirecionar para login
    navigate('/login');
    
    // Mostrar mensagem de sucesso
    toast.success('Logout realizado com sucesso!');
  }, [navigate]);

  // Verificar token na inicialização
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Configurar token no axios
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Verificar se o token é válido
          const response = await api.get('/auth/me');
          
          if (response.data.success) {
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: response.data.usuario,
                token
              }
            });
          } else {
            // Token inválido, limpar
            logout();
          }
        } catch (error) {
          console.error('Erro ao verificar token:', error);
          logout();
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, [logout]);

  // Função de login
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.success) {
        const { usuario, token } = response.data;
        
        // Salvar token no localStorage
        localStorage.setItem('token', token);
        
        // Configurar token no axios
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Atualizar estado
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: usuario, token }
        });
        
        toast.success('Login realizado com sucesso!');
        
        // Redirecionar baseado no perfil
        if (usuario.perfil === 'motorista') {
          navigate('/ponto');
        } else {
          navigate('/dashboard');
        }
        
        return { success: true };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      
      let errorMessage = 'Erro ao fazer login';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      toast.error(errorMessage);
      
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Função para atualizar dados do usuário
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_USER,
      payload: userData
    });
  };

  // Função para alterar senha
  const changePassword = async (passwordData) => {
    try {
      const response = await api.post('/auth/alterar-senha', passwordData);
      
      if (response.data.success) {
        toast.success('Senha alterada com sucesso!');
        return { success: true };
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      
      let errorMessage = 'Erro ao alterar senha';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      toast.error(errorMessage);
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Função para renovar token
  const refreshToken = async () => {
    try {
      const response = await api.post('/auth/refresh');
      
      if (response.data.success) {
        const { token } = response.data;
        
        // Atualizar token no localStorage
        localStorage.setItem('token', token);
        
        // Configurar novo token no axios
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });
        
        return { success: true };
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      logout();
      return { success: false };
    }
  };

  // Função para verificar permissões
  const hasPermission = (modulo, acao) => {
    if (!state.user) return false;
    
    // Administradores têm acesso total
    if (state.user.perfil === 'administrador') return true;
    
    // Verificar permissões específicas
    if (state.user.permissoes && state.user.permissoes.length > 0) {
      const permissao = state.user.permissoes.find(p => p.modulo === modulo);
      if (permissao && permissao.acoes.includes(acao)) {
        return true;
      }
    }
    
    // Verificar permissões baseadas no perfil
    const permissoesPorPerfil = {
      supervisor: {
        ponto: ['visualizar', 'aprovar', 'editar'],
        frota: ['visualizar', 'criar', 'editar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      },
      financeiro: {
        ponto: ['visualizar'],
        frota: ['visualizar'],
        rh: ['visualizar'],
        financeiro: ['visualizar', 'criar', 'editar'],
        relatorios: ['visualizar']
      },
      motorista: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar']
      },
      monitor: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar'],
        chamada: ['visualizar', 'criar', 'editar']
      },
      limpeza: {
        ponto: ['visualizar', 'criar'],
        frota: ['visualizar']
      },
      administrativo: {
        ponto: ['visualizar', 'aprovar'],
        frota: ['visualizar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      },
      coordenador: {
        ponto: ['visualizar', 'aprovar', 'editar'],
        frota: ['visualizar', 'criar', 'editar'],
        rh: ['visualizar', 'editar'],
        relatorios: ['visualizar']
      }
    };
    
    const permissoesPerfil = permissoesPorPerfil[state.user.perfil];
    
    return permissoesPerfil && 
           permissoesPerfil[modulo] && 
           permissoesPerfil[modulo].includes(acao);
  };

  // Função para verificar se é um perfil específico
  const isProfile = (perfil) => {
    return state.user?.perfil === perfil;
  };

  // Função para verificar se é motorista
  const isMotorista = () => isProfile('motorista');
  
  // Função para verificar se é supervisor
  const isSupervisor = () => isProfile('supervisor');
  
  // Função para verificar se é financeiro
  const isFinanceiro = () => isProfile('financeiro');
  
  // Função para verificar se é administrador
  const isAdmin = () => isProfile('administrador');

  // Valor do contexto
  const value = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    isAuthenticated: state.isAuthenticated,
    login,
    logout,
    updateUser,
    changePassword,
    refreshToken,
    hasPermission,
    isProfile,
    isMotorista,
    isSupervisor,
    isFinanceiro,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

